import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Box, Typography, Grid, Button, Stack, Chip, IconButton, Tooltip, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment, Slider,
    Snackbar, Alert, Divider, Autocomplete, MenuItem,
} from '@mui/material';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK } from '../theme';
import { card, solidBtn, ghostBtn, field, EmptyState, ConfirmDialog, LoadAlert } from '../components/uiKit';
import { initialsFromName as initials, paletteColor as colorFor } from '../utils/format';
import http, { apiErrorMessage } from '../Api/http';
import { SkeletonRows } from '../components/uiKit';
import { useCrumbActionsSetter } from '../utils/crumbActions';
import {
    GetClientLocations, GetClientLocationById, CreateClientLocation, UpdateClientLocation, DeleteClientLocation,
    GetLocationAssignments, AssignLocation, UpdateLocationAssignment, DeleteLocationAssignment, getEmployees,
} from '../Api/Api';

const INDIA_CENTER = [20.5937, 78.9629];
const DEFAULT_RADIUS = 150;
const RADIUS_MIN = 10;
const RADIUS_MAX = 500;

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDay = (d) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || ''));
    if (!m) return d || '—';
    return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const assignStatus = (from, to) => {
    const t = todayISO();
    if (from && t < from) return { label: 'Upcoming', c: '#0369A1', bg: '#E0F2FE' };
    if (to && t > to) return { label: 'Ended', c: '#64748B', bg: '#F1F5F9' };
    return { label: 'Active', c: '#16A34A', bg: '#DCFCE7' };
};

const pinIcon = L.divIcon({
    className: 'ara-pin',
    html: `<svg width="30" height="42" viewBox="0 0 30 42" fill="none" xmlns="http://www.w3.org/2000/svg" style="display:block">
        <path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 27 15 27s15-16.5 15-27C30 6.7 23.3 0 15 0z" fill="#7C5CFC"/>
        <circle cx="15" cy="15" r="6" fill="#fff"/>
    </svg>`,
    iconSize: [30, 42],
    iconAnchor: [15, 42],
});

const fmtCoord = (n) => (typeof n === 'number' && !Number.isNaN(n) ? n.toFixed(6) : '—');

function MapPicker({ position, radius, onPick, focus }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const circleRef = useRef(null);

    useEffect(() => {
        if (mapRef.current || !containerRef.current) return undefined;
        const start = position ? [position.lat, position.lng] : INDIA_CENTER;
        const map = L.map(containerRef.current, { center: start, zoom: position ? 16 : 5, attributionControl: false });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map);
        map.on('click', (e) => onPick(e.latlng.lat, e.latlng.lng));
        mapRef.current = map;
        const t = setTimeout(() => map.invalidateSize(), 220);
        return () => { clearTimeout(t); map.remove(); mapRef.current = null; markerRef.current = null; circleRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !position) return;
        const latlng = [position.lat, position.lng];
        if (!markerRef.current) {
            markerRef.current = L.marker(latlng, { icon: pinIcon, draggable: true }).addTo(map);
            markerRef.current.on('dragend', () => { const p = markerRef.current.getLatLng(); onPick(p.lat, p.lng); });
        } else {
            markerRef.current.setLatLng(latlng);
        }
        if (!circleRef.current) {
            circleRef.current = L.circle(latlng, { radius, color: PRIMARY, fillColor: PRIMARY, fillOpacity: 0.12, weight: 2 }).addTo(map);
        } else {
            circleRef.current.setLatLng(latlng);
            circleRef.current.setRadius(radius);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [position?.lat, position?.lng, radius]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !focus) return;
        map.flyTo([focus.lat, focus.lng], 16, { duration: 0.6 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focus?.at]);

    return <Box ref={containerRef} sx={{ height: 340, width: '100%', borderRadius: '9px', overflow: 'hidden', border: '1px solid #E5E7EB', bgcolor: '#EAEFF4' }} />;
}

const EMPTY_FORM = { id: null, name: '', clientName: '', address: '', lat: null, lng: null, radius: DEFAULT_RADIUS, isActive: true };

export default function ClientLocationsPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [tried, setTried] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadingOne, setLoadingOne] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [search, setSearch] = useState('');
    const [searching, setSearching] = useState(false);
    const [locating, setLocating] = useState(false);
    const [focus, setFocus] = useState(null);
    const focusOn = (lat, lng) => setFocus((f) => ({ lat: Number(lat), lng: Number(lng), at: (f?.at || 0) + 1 }));
    const [snack, setSnack] = useState(null);
    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const normalize = (r) => ({
        id: r.id ?? r.Id,
        name: r.name ?? r.Name ?? '',
        clientName: r.clientCompany ?? r.ClientCompany ?? r.clientName ?? r.ClientName ?? r.client ?? '',
        address: r.address ?? r.Address ?? '',
        lat: Number(r.latitude ?? r.Latitude),
        lng: Number(r.longitude ?? r.Longitude),
        radius: Number(r.radiusMeters ?? r.RadiusMeters ?? DEFAULT_RADIUS),
        isActive: (r.isActive ?? r.IsActive) !== false,
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetClientLocations);
            if (body?.error) throw new Error(body.message || 'Could not load client locations.');
            const list = Array.isArray(body?.data) ? body.data : (body?.data?.locations || []);
            setRows(list.map(normalize));
            setLoadError('');
        } catch (err) {
            if (err?.response?.status === 404) { setRows([]); setLoadError(''); }
            else setLoadError(apiErrorMessage(err, 'Could not load client locations.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const ASSIGN_EMPTY = { locationId: '', employees: [], fromDate: todayISO(), toDate: todayISO() };
    const [employees, setEmployees] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [loadingAssign, setLoadingAssign] = useState(true);
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignForm, setAssignForm] = useState(ASSIGN_EMPTY);
    const [assignTried, setAssignTried] = useState(false);
    const [savingAssign, setSavingAssign] = useState(false);
    const [confirmAssign, setConfirmAssign] = useState(null);
    const [deletingAssignId, setDeletingAssignId] = useState(null);
    const [editingAssign, setEditingAssign] = useState(null);

    const loadEmployees = useCallback(async () => {
        try {
            const { data: body } = await http.get(getEmployees);
            const list = Array.isArray(body?.data) ? body.data : [];
            setEmployees(list
                .map((e) => ({ code: e.employeeCode ?? e.rollNumber ?? '', name: e.name ?? '', designation: e.designation ?? '', department: e.department ?? '' }))
                .filter((e) => e.code));
        } catch { setEmployees([]); }
    }, []);

    const loadAssignments = useCallback(async () => {
        setLoadingAssign(true);
        try {
            const { data: body } = await http.get(GetLocationAssignments);
            const list = Array.isArray(body?.data) ? body.data : (body?.data?.assignments || []);
            setAssignments(list.map((a) => ({
                id: a.id ?? a.Id,
                clientLocationId: a.clientLocationId ?? a.ClientLocationId,
                locationName: a.locationName ?? a.LocationName ?? '',
                employeeCode: a.employeeCode ?? a.EmployeeCode ?? '',
                employeeName: a.employeeName ?? a.EmployeeName ?? '',
                fromDate: String(a.fromDate ?? a.FromDate ?? '').slice(0, 10),
                toDate: String(a.toDate ?? a.ToDate ?? '').slice(0, 10),
            })));
        } catch { setAssignments([]); } finally { setLoadingAssign(false); }
    }, []);

    useEffect(() => { loadEmployees(); loadAssignments(); }, [loadEmployees, loadAssignments]);

    const openAssign = (loc) => {
        setEditingAssign(null);
        setAssignForm({ ...ASSIGN_EMPTY, locationId: loc?.id ?? (rows[0]?.id ?? ''), fromDate: todayISO(), toDate: todayISO() });
        setAssignTried(false);
        setAssignOpen(true);
    };
    const openEditAssign = (a) => {
        setEditingAssign(a);
        setAssignForm({ locationId: a.clientLocationId ?? '', employees: [], fromDate: a.fromDate, toDate: a.toDate });
        setAssignTried(false);
        setAssignOpen(true);
    };
    const closeAssign = () => { if (!savingAssign) setAssignOpen(false); };
    const locationOptions = rows.filter((r) => r.isActive || r.id === editingAssign?.clientLocationId);

    const assignValid = assignForm.locationId
        && (editingAssign || assignForm.employees.length > 0)
        && assignForm.fromDate && assignForm.toDate
        && assignForm.fromDate <= assignForm.toDate;

    const saveAssign = async () => {
        setAssignTried(true);
        if (editingAssign && assignValid) {
            setSavingAssign(true);
            try {
                const { data: body } = await http.put(UpdateLocationAssignment, {
                    id: editingAssign.id,
                    employeeCode: editingAssign.employeeCode,
                    clientLocationId: assignForm.locationId,
                    fromDate: assignForm.fromDate,
                    toDate: assignForm.toDate,
                });
                if (body?.error) throw new Error(body.message || 'Could not update the assignment.');
                notify('Assignment updated.');
                setAssignOpen(false);
                await loadAssignments();
            } catch (err) {
                notify(apiErrorMessage(err, 'Could not update the assignment.'), 'error');
            } finally { setSavingAssign(false); }
            return;
        }
        if (!assignValid) {
            notify(!assignForm.locationId ? 'Pick a location.'
                : !editingAssign && assignForm.employees.length === 0 ? 'Select at least one staff member.'
                    : assignForm.fromDate > assignForm.toDate ? 'The "to" date is before the "from" date.'
                        : 'Complete the assignment details.', 'warning');
            return;
        }
        setSavingAssign(true);
        try {
            const results = await Promise.allSettled(assignForm.employees.map((e) =>
                http.post(AssignLocation, {
                    employeeCode: e.code,
                    clientLocationId: assignForm.locationId,
                    fromDate: assignForm.fromDate,
                    toDate: assignForm.toDate,
                }).then(({ data }) => {
                    if (data?.error) throw new Error(data.message || 'failed');
                    return e.code;
                })));
            const ok = results.filter((r) => r.status === 'fulfilled').length;
            const failed = results.length - ok;
            if (ok > 0) {
                notify(failed
                    ? `Assigned ${ok} of ${results.length}. ${failed} skipped — already posted here over overlapping dates.`
                    : `Assigned ${ok} staff member${ok > 1 ? 's' : ''}.`, failed ? 'warning' : 'success');
                setAssignOpen(false);
                await loadAssignments();
            } else {
                notify('Could not save the assignment. They may already be posted here over these dates.', 'error');
            }
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the assignment.'), 'error');
        } finally { setSavingAssign(false); }
    };

    const removeAssign = async (a) => {
        setConfirmAssign(null);
        setDeletingAssignId(a.id);
        try {
            const { data: body } = await http.delete(DeleteLocationAssignment, { params: { id: a.id } });
            if (body?.error) throw new Error(body.message || 'Could not remove the assignment.');
            notify('Assignment removed.');
            await loadAssignments();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not remove the assignment.'), 'error');
        } finally { setDeletingAssignId(null); }
    };

    const openAdd = () => { setForm(EMPTY_FORM); setTried(false); setSearch(''); setFocus(null); setDialogOpen(true); };
    const openEdit = async (r) => {
        setForm({ ...r }); setTried(false); setSearch(''); setFocus(null); setDialogOpen(true);
        // Refresh the form from the server copy; the list row stays as the fallback.
        setLoadingOne(true);
        try {
            const { data: body } = await http.get(GetClientLocationById, { params: { id: r.id } });
            if (body?.error || !body?.data) return;
            const fresh = normalize(body.data);
            if (fresh.id == null || Number.isNaN(fresh.lat) || Number.isNaN(fresh.lng)) return;
            setForm((f) => (f.id === r.id ? { ...f, ...fresh } : f));
            focusOn(fresh.lat, fresh.lng);
        } catch {
            // keep the list row
        } finally {
            setLoadingOne(false);
        }
    };
    const closeDialog = () => { if (!saving) setDialogOpen(false); };

    const pick = (lat, lng) => setForm((f) => ({ ...f, lat: Number(lat), lng: Number(lng) }));

    const runSearch = async () => {
        const q = search.trim();
        if (!q) return;
        setSearching(true);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`);
            const results = await res.json();
            if (Array.isArray(results) && results.length) {
                const { lat, lon, display_name: label } = results[0];
                setForm((f) => ({ ...f, lat: Number(lat), lng: Number(lon), address: f.address || label || '' }));
                focusOn(lat, lon);
            } else {
                notify('No place found for that search.', 'warning');
            }
        } catch {
            notify('Address search is unavailable right now — drop the pin manually.', 'warning');
        } finally {
            setSearching(false);
        }
    };

    const useMyLocation = () => {
        if (!navigator.geolocation) { notify('This browser can’t read your location.', 'warning'); return; }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => { pick(pos.coords.latitude, pos.coords.longitude); focusOn(pos.coords.latitude, pos.coords.longitude); setLocating(false); },
            () => { notify('Couldn’t read your location — allow permission or drop the pin manually.', 'warning'); setLocating(false); },
            { enableHighAccuracy: true, timeout: 10000 },
        );
    };

    const valid = Boolean(form.name.trim()) && form.lat != null && form.lng != null;

    const save = async () => {
        setTried(true);
        if (!valid) {
            notify(!form.name.trim() ? 'Give the location a name.' : 'Pick the spot on the map first.', 'warning');
            return;
        }
        const payload = {
            name: form.name.trim(),
            clientCompany: form.clientName.trim(),
            address: form.address.trim(),
            latitude: form.lat,
            longitude: form.lng,
            radiusMeters: form.radius,
            isActive: form.isActive,
        };
        setSaving(true);
        try {
            const { data: body } = form.id
                ? await http.put(UpdateClientLocation, { id: form.id, ...payload })
                : await http.post(CreateClientLocation, payload);
            if (body?.error) throw new Error(body.message || 'Could not save the location.');
            notify(form.id ? 'Location updated.' : 'Location added.');
            setDialogOpen(false);
            await load();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the location.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        setConfirm(null);
        setDeletingId(row.id);
        try {
            const { data: body } = await http.delete(DeleteClientLocation, { params: { id: row.id } });
            if (body?.error) throw new Error(body.message || 'Could not delete the location.');
            notify('Location removed.');
            await load();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not delete the location.'), 'error');
        } finally {
            setDeletingId(null);
        }
    };

    // The page actions live at the right end of the breadcrumb row. Handlers are read through a ref
    // so the buttons only need re-publishing when their enabled state changes.
    const setCrumbActions = useCrumbActionsSetter();
    const actionsRef = useRef({});
    actionsRef.current = { load, openAdd, openAssign };
    const noRows = rows.length === 0;
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip arrow title="Reload">
                    <span>
                        <IconButton onClick={() => actionsRef.current.load()} disabled={loading} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', color: '#64748B', height: 42, width: 42, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                            <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Button startIcon={<PersonAddAlt1RoundedIcon />} onClick={() => actionsRef.current.openAssign(null)} disabled={noRows}
                    sx={{ ...ghostBtn, height: 42, px: 2, '&.Mui-disabled': { opacity: 0.5 } }}>Assign Staff</Button>
                <Button startIcon={<AddRoundedIcon />} onClick={() => actionsRef.current.openAdd()} sx={{ ...solidBtn, height: 42, px: 2.2 }}>Add Location</Button>
            </Box>,
        );
        return () => setCrumbActions(null);
    }, [loading, noRows, setCrumbActions]);

    return (
        <Box sx={{ p: 2 }}>
            {loading ? (
                <SkeletonRows count={5} />
            ) : loadError ? (
                <Box sx={{ ...card, p: 3 }}>
                    <LoadAlert sx={{ borderRadius: '9px' }} action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{loadError}</LoadAlert>
                </Box>
            ) : rows.length === 0 ? (
                <Box sx={{ ...card, p: 0 }}>
                    <EmptyState icon={PlaceRoundedIcon} title="No client locations yet" hint="Add a work site and pin it on the map so field staff can check in when they arrive." />
                </Box>
            ) : (
                <Grid container spacing={1.6}>
                    {rows.map((r) => {
                        const busy = deletingId === r.id;
                        return (
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={r.id}>
                                <Box sx={{ ...card, p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1, opacity: busy ? 0.5 : 1 }}>
                                    <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                        <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', minWidth: 0 }}>
                                            <Box sx={{ width: 38, height: 38, borderRadius: '9px', bgcolor: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <PlaceRoundedIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: '#0F172A' }} noWrap>{r.name}</Typography>
                                                {r.clientName && <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }} noWrap>{r.clientName}</Typography>}
                                            </Box>
                                        </Stack>
                                        <Chip label={r.isActive ? 'Active' : 'Inactive'} size="small" sx={{ flexShrink: 0, height: 22, fontSize: 10.5, fontWeight: 800, bgcolor: r.isActive ? '#DCFCE7' : '#F1F5F9', color: r.isActive ? '#16A34A' : '#64748B' }} />
                                    </Stack>

                                    {r.address && <Typography sx={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>{r.address}</Typography>}

                                    <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8, mt: 'auto', pt: 0.5 }}>
                                        <Chip size="small" label={`${fmtCoord(r.lat)}, ${fmtCoord(r.lng)}`} sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1F5F9', color: '#475569', fontFamily: 'monospace' }} />
                                        <Chip size="small" label={`Radius ${r.radius} m`} sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#E0F2FE', color: '#0369A1' }} />
                                    </Stack>

                                    <Divider sx={{ borderColor: '#F1F3F7', mt: 0.5 }} />
                                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Button size="small" disabled={busy} onClick={() => openAssign(r)} startIcon={<PersonAddAlt1RoundedIcon sx={{ fontSize: 16 }} />}
                                            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, color: PRIMARY, bgcolor: PRIMARY_LIGHT, borderRadius: '7px', px: 1.2, height: 30, '&:hover': { bgcolor: '#E7DFFC' } }}>
                                            Assign staff
                                        </Button>
                                        <Box>
                                        <Tooltip arrow title="Edit">
                                            <span><IconButton size="small" disabled={busy} onClick={() => openEdit(r)} sx={{ color: PRIMARY, '&:hover': { bgcolor: PRIMARY_LIGHT } }}><EditRoundedIcon sx={{ fontSize: 17 }} /></IconButton></span>
                                        </Tooltip>
                                        <Tooltip arrow title="Delete">
                                            <span><IconButton size="small" disabled={busy} onClick={() => setConfirm(r)} sx={{ color: '#98A0AE', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>{busy ? <CircularProgress size={15} /> : <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />}</IconButton></span>
                                        </Tooltip>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            <Box sx={{ ...card, p: 0, overflow: 'hidden', mt: 2 }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, p: 1.8, bgcolor: '#F7F6FD', borderBottom: '1px solid #EAE7F7', flexWrap: 'wrap' }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: '#fff', boxShadow: '0 1px 4px rgba(16,24,40,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <GroupsRoundedIcon sx={{ color: PRIMARY, fontSize: 18 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: '#0F172A' }}>Staff Assignments</Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>Who is posted to a site, and for which dates — geofence check-in applies only within these dates.</Typography>
                    </Box>
                    <Button startIcon={<PersonAddAlt1RoundedIcon sx={{ fontSize: 16 }} />} onClick={() => openAssign(null)} disabled={rows.length === 0}
                        sx={{ ...solidBtn, height: 34, px: 1.8, fontSize: 12.5, ml: 'auto', '&.Mui-disabled': { opacity: 0.5 } }}>Assign</Button>
                </Stack>

                {loadingAssign ? (
                    <SkeletonRows count={5} />
                ) : (
                    <Box sx={{ p: 1.4, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {assignments.map((a) => {
                            const st = assignStatus(a.fromDate, a.toDate);
                            const busy = deletingAssignId === a.id;
                            return (
                                <Box key={a.id} sx={{ border: '1px solid #EEF0F6', borderRadius: '9px', p: 1.4, display: 'flex', alignItems: 'center', gap: 1.4, flexWrap: 'wrap', opacity: busy ? 0.5 : 1 }}>
                                    <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: `${colorFor(a.employeeName)}18`, color: colorFor(a.employeeName), fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initials(a.employeeName)}</Box>
                                    <Box sx={{ minWidth: 150 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{a.employeeName || a.employeeCode}</Typography>
                                        <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{a.employeeCode}</Typography>
                                    </Box>
                                    <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', minWidth: 160 }}>
                                        <PlaceRoundedIcon sx={{ fontSize: 15, color: PRIMARY }} />
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#475569' }}>{a.locationName || '—'}</Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', flex: 1, minWidth: 190 }}>
                                        <CalendarMonthRoundedIcon sx={{ fontSize: 15, color: '#94A3B8' }} />
                                        <Typography sx={{ fontSize: 12, color: '#475569' }}>{fmtDay(a.fromDate)} → {fmtDay(a.toDate)}</Typography>
                                    </Stack>
                                    <Chip label={st.label} size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 800, bgcolor: st.bg, color: st.c }} />
                                    <Tooltip arrow title="Edit assignment">
                                        <span><IconButton size="small" disabled={busy} onClick={() => openEditAssign(a)} sx={{ color: PRIMARY, '&:hover': { bgcolor: PRIMARY_LIGHT } }}><EditRoundedIcon sx={{ fontSize: 17 }} /></IconButton></span>
                                    </Tooltip>
                                    <Tooltip arrow title="Remove assignment">
                                        <span><IconButton size="small" disabled={busy} onClick={() => setConfirmAssign(a)} sx={{ color: '#98A0AE', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>{busy ? <CircularProgress size={14} /> : <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />}</IconButton></span>
                                    </Tooltip>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            <Dialog open={assignOpen} onClose={closeAssign} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{editingAssign ? 'Edit Assignment' : 'Assign Staff to a Location'}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>{editingAssign ? 'Change the site or the dates for this posting.' : 'Pick the site, the staff, and the dates they’ll work there.'}</Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Stack spacing={2}>
                        <TextField select label="Client location *" size="small" fullWidth value={assignForm.locationId}
                            onChange={(e) => setAssignForm((f) => ({ ...f, locationId: e.target.value }))}
                            error={assignTried && !assignForm.locationId} sx={field}
                            InputProps={{ startAdornment: <InputAdornment position="start"><PlaceRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} /></InputAdornment> }}>
                            {(editingAssign ? locationOptions : rows).map((r) => <MenuItem key={r.id} value={r.id} sx={{ fontSize: 13.5 }}>{r.name}{r.clientName ? ` — ${r.clientName}` : ''}{r.isActive ? '' : ' (inactive)'}</MenuItem>)}
                        </TextField>

                        {editingAssign ? (
                            <TextField label="Staff" size="small" fullWidth disabled sx={field}
                                value={`${editingAssign.employeeName || editingAssign.employeeCode} (${editingAssign.employeeCode})`} />
                        ) : (
                        <Autocomplete
                            multiple options={employees} value={assignForm.employees}
                            onChange={(_, v) => setAssignForm((f) => ({ ...f, employees: v }))}
                            getOptionLabel={(o) => `${o.name || o.code} (${o.code})`}
                            isOptionEqualToValue={(o, v) => o.code === v.code}
                            renderInput={(params) => (
                                <TextField {...params} label="Staff *" size="small" placeholder={assignForm.employees.length ? '' : 'Search staff…'}
                                    error={assignTried && assignForm.employees.length === 0} sx={field} />
                            )}
                            renderTags={(value, getTagProps) => value.map((o, i) => (
                                <Chip {...getTagProps({ index: i })} key={o.code} label={o.name || o.code} size="small"
                                    sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY_DARK, fontWeight: 700, fontSize: 11.5 }} />
                            ))}
                        />
                        )}

                        <Stack direction="row" spacing={1.5}>
                            <TextField type="date" label="From *" size="small" fullWidth value={assignForm.fromDate}
                                onChange={(e) => setAssignForm((f) => ({ ...f, fromDate: e.target.value }))}
                                error={assignTried && !assignForm.fromDate} sx={field} slotProps={{ inputLabel: { shrink: true } }} />
                            <TextField type="date" label="To *" size="small" fullWidth value={assignForm.toDate}
                                onChange={(e) => setAssignForm((f) => ({ ...f, toDate: e.target.value }))}
                                error={assignTried && (!assignForm.toDate || assignForm.toDate < assignForm.fromDate)} sx={field} slotProps={{ inputLabel: { shrink: true } }} />
                        </Stack>

                        <Box sx={{ p: 1.2, borderRadius: '8px', bgcolor: '#F8FAFC', border: '1px solid #EEF1F6' }}>
                            <Typography sx={{ fontSize: 11, color: '#6B7280', lineHeight: 1.5 }}>
                                While an assignment is active, the selected staff can only mark attendance from <strong>inside this location’s radius</strong>. Outside the dates, or off-site, the mobile app blocks their check-in.
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeAssign} disabled={savingAssign} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={saveAssign} disabled={savingAssign} startIcon={savingAssign ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.6 }}>
                        {editingAssign ? (savingAssign ? 'Saving…' : 'Save Changes') : (savingAssign ? 'Assigning…' : 'Assign')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirmAssign)}
                onClose={() => setConfirmAssign(null)}
                onConfirm={() => removeAssign(confirmAssign)}
                title="Remove this assignment?"
                body={confirmAssign ? `${confirmAssign.employeeName || confirmAssign.employeeCode} will no longer be tied to ${confirmAssign.locationName} for those dates.` : ''}
                confirmLabel="Remove"
            />

            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{form.id ? 'Edit Client Location' : 'Add Client Location'}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>Search or drop a pin on the map, then set the check-in radius.</Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Stack spacing={1.6}>
                                <TextField label="Location name *" size="small" fullWidth value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    error={tried && !form.name.trim()} placeholder="e.g. Acme Corp — Head Office" sx={field}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><PlaceRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} /></InputAdornment> }} />
                                <TextField label="Client / Company" size="small" fullWidth value={form.clientName}
                                    onChange={(e) => setForm((f) => ({ ...f, clientName: e.target.value }))} sx={field}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><BusinessRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} /></InputAdornment> }} />
                                <TextField label="Address" size="small" fullWidth multiline minRows={2} value={form.address}
                                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} sx={field} />

                                <Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', mb: 0.4 }}>Check-in radius — {form.radius} m</Typography>
                                    <Slider value={form.radius} min={RADIUS_MIN} max={RADIUS_MAX} step={1}
                                        onChange={(_, v) => setForm((f) => ({ ...f, radius: v }))}
                                        valueLabelDisplay="auto" valueLabelFormat={(v) => `${v} m`}
                                        sx={{ color: PRIMARY, '& .MuiSlider-thumb': { boxShadow: `0 0 0 6px ${PRIMARY}22` } }} />
                                    <Typography sx={{ fontSize: 10.5, color: '#94A3B8' }}>Staff can mark attendance anywhere within this distance of the pin. 100–150 m absorbs normal GPS drift.</Typography>
                                </Box>

                                <Box sx={{ p: 1.2, borderRadius: '8px', bgcolor: '#F8FAFC', border: '1px solid #EEF1F6' }}>
                                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.5 }}>Selected coordinates</Typography>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: form.lat != null ? '#0F172A' : '#CBD2DD', fontFamily: 'monospace', mt: 0.3 }}>
                                        {form.lat != null ? `${fmtCoord(form.lat)}, ${fmtCoord(form.lng)}` : 'Click the map to drop a pin'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, md: 7 }}>
                            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                <TextField size="small" fullWidth placeholder="Search an address or place…" value={search}
                                    onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }} sx={field}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} /></InputAdornment> }} />
                                <Button onClick={runSearch} disabled={searching || !search.trim()} sx={{ ...ghostBtn, height: 40, px: 1.6, minWidth: 0 }}>
                                    {searching ? <CircularProgress size={16} /> : 'Search'}
                                </Button>
                                <Tooltip arrow title="Use my current location">
                                    <span><IconButton onClick={useMyLocation} disabled={locating} sx={{ border: '1px solid #E5E7EB', borderRadius: '7px', color: PRIMARY, width: 40, height: 40 }}>
                                        {locating ? <CircularProgress size={16} /> : <MyLocationRoundedIcon sx={{ fontSize: 18 }} />}
                                    </IconButton></span>
                                </Tooltip>
                            </Stack>
                            {dialogOpen && (
                                <MapPicker position={form.lat != null ? { lat: form.lat, lng: form.lng } : null} radius={form.radius} onPick={pick} focus={focus} />
                            )}
                            <Typography sx={{ fontSize: 10.5, color: '#94A3B8', mt: 0.6 }}>Map © OpenStreetMap contributors · click or drag the pin to set the exact spot.</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeDialog} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={save} disabled={saving || loadingOne} startIcon={(saving || loadingOne) ?<CircularProgress size={15} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.6 }}>
                        {saving ? 'Saving…' : form.id ? 'Save Location' : 'Add Location'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirm)}
                onClose={() => setConfirm(null)}
                onConfirm={() => remove(confirm)}
                title="Delete this location?"
                body={confirm ? `"${confirm.name}" will be removed. Staff will no longer be able to check in there.` : ''}
                confirmLabel="Delete"
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={3800} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
